import { useTranslation } from 'react-i18next';
import { IconFont } from '@/components';
import { CheckExpandable } from '@/components/CheckExpandable';
import { LargeSelect } from '@/components/LargeSelect';
import { SectionHeader } from '@/components/SectionHeader';
import { useFormSyncState } from '@/hooks';
import { Card, Form, Input, InputNumber } from '@arco-design/web-react';
import { useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { useIsMounted } from 'usehooks-ts';
import { GrayBox } from '../tools';
import { WorkloadComponent, WorkloadType } from '../types';
import { UpdateStrategyValues, useUpdateStrategyStore } from './useUpdateStrategyStore';

const FormItem = Form.Item;
const { ROLLING_UPDATE, RECREATE, ON_DELETE } = UpdateStrategyValues;
const { DEPLOYMENT, STATEFULSET, DAEMONSET } = WorkloadType;

interface UpdateStrategyProps extends WorkloadComponent {}

export function UpdateStrategy({ module = DEPLOYMENT }: UpdateStrategyProps) {
  const { t } = useTranslation();

  const msg = {
    update_strategy: t('components:update.strategy'),
    simultaneous_update: t('components:simultaneous.update'),
    simultaneous_update_desc: t(
      'components:delete.all.old.pod.replicas.before.creating.new.ones.service.will.be.interrupted.during.upgrade',
    ),
    rolling_update: t('components:rolling.update'),
    on_delete: t('components:update.on.delete'),
    rolling_update_desc: t(
      'components:gradually.replace.old.pod.replicas.with.new.ones.during.upgrade.traffic.will.be.load.balanced.between.old.and.new.pods.service.will.not.be.interrupted',
    ),
    on_delete_desc: t('components:need.to.manually.delete.pod.replicas.to.update.them'),
    rolling_update_settings: t('components:rolling.update.settings'),
    max_unavailable_pods: t('components:maximum.unavailable.pods'),
    max_unavailable_pods_desc: t(
      'components:set.the.maximum.number.or.percentage.of.unavailable.pod.replicas.allowed.during.update',
    ),
    max_unavailable_pods_error_empty: t('components:please.enter.maximum.unavailable.pods'),
    partition: t('components:pod.replica.partition'),
    partition_desc: t(
      'components:set.a.partition.to.divide.pod.replicas.into.two.groups.when.updating.statefulset.only.pods.with.ordinal.partition.will.be.updated',
    ),
    partition_error_empty: t('components:please.set.pod.replica.partition'),
    max_unavailable_pods_error_invalid: t('components:please.enter.an.integer.or.percentage'),
    max_extra_pods: t('components:maximum.surge.pods'),
    max_extra_pods_desc: t(
      'components:maximum.number.or.percentage.of.extra.pod.replicas.allowed.during.update',
    ),
    max_extra_pods_error_empty: t('components:please.enter.maximum.surge.pods'),
    max_extra_pods_error_invalid: t('components:please.enter.an.integer.or.percentage'),
    min_ready_seconds: t('components:minimum.pod.ready.duration.s'),
    min_ready_seconds_desc: t(
      'components:minimum.stable.running.duration.for.pod.replicas.to.be.considered.ready',
    ),
    min_ready_seconds_error_empty: t(
      'components:please.set.the.minimum.stable.running.duration.for.pod.replicas.to.be.considered.ready',
    ),
  };

  const {
    updateStrategy,
    setUpdateStrategy,
    maxUnavailablePods,
    maxExtraPods,
    partition,
    minReadySeconds,
    updateMaxUnavailablePods,
    updateMaxExtraPods,
    updatePartition,
    updateMinReadySeconds,
    reset,
  } = useUpdateStrategyStore();
  const [form] = Form.useForm();
  useFormSyncState(form, { maxUnavailablePods, maxExtraPods, partition, minReadySeconds });
  const isMounted = useIsMounted();

  useEffect(() => {
    if (isMounted() && module !== DEPLOYMENT) {
      reset(module);
    }
  }, [isMounted, reset, module]);

  /** 初始化默认选项 */
  useEffect(() => {
    switch (module) {
      case DAEMONSET:
      case STATEFULSET:
      case DEPLOYMENT:
        setUpdateStrategy(module, ROLLING_UPDATE);
        break;
      default:
        break;
    }
  }, [setUpdateStrategy, module]);

  const options = useMemo(() => {
    switch (module) {
      case DAEMONSET:
        return [
          {
            label: (
              <LargeSelect.IconLabel
                label={msg.rolling_update}
                description={msg.rolling_update_desc}
                icon={<IconFont name="icon-tongshigengxin" style={{ fontSize: 36 }} />}
              />
            ),
            value: ROLLING_UPDATE,
          },
          {
            label: (
              <LargeSelect.IconLabel
                label={msg.on_delete}
                description={msg.on_delete_desc}
                icon={<IconFont name="icon-tongshigengxin" style={{ fontSize: 36 }} />}
              />
            ),
            value: ON_DELETE,
          },
        ];
      case STATEFULSET:
        return [
          {
            label: (
              <LargeSelect.IconLabel
                label={msg.rolling_update}
                description={msg.rolling_update_desc}
                icon={<IconFont name="icon-tongshigengxin" style={{ fontSize: 36 }} />}
              />
            ),
            value: ROLLING_UPDATE,
          },
          {
            label: (
              <LargeSelect.IconLabel
                label={msg.on_delete}
                description={msg.on_delete_desc}
                icon={<IconFont name="icon-tongshigengxin" style={{ fontSize: 36 }} />}
              />
            ),
            value: ON_DELETE,
          },
        ];
      case DEPLOYMENT:
      default:
        return [
          {
            label: (
              <LargeSelect.IconLabel
                label={msg.rolling_update}
                description={msg.rolling_update_desc}
                icon={<IconFont name="icon-tongshigengxin" style={{ fontSize: 36 }} />}
              />
            ),
            value: ROLLING_UPDATE,
          },
          {
            label: (
              <LargeSelect.IconLabel
                label={msg.simultaneous_update}
                description={msg.simultaneous_update_desc}
                icon={<IconFont name="icon-tongshigengxin" style={{ fontSize: 36 }} />}
              />
            ),
            value: RECREATE,
          },
        ];
    }
  }, [module, msg]);

  const isDeployment =
    module === DEPLOYMENT && updateStrategy === UpdateStrategyValues.ROLLING_UPDATE;
  const isStatefulSet =
    module === STATEFULSET && updateStrategy === UpdateStrategyValues.ROLLING_UPDATE;
  const isDaemonSet =
    module === DAEMONSET && updateStrategy === UpdateStrategyValues.ROLLING_UPDATE;
  const Header = <SectionHeader inline title={msg.rolling_update_settings} />;

  return (
    <Form
      layout="vertical"
      form={form}
      initialValues={{
        maxUnavailablePods,
        maxExtraPods,
        partition,
        minReadySeconds,
      }}
      onValuesChange={(_, values) => {
        updateMaxUnavailablePods(values.maxUnavailablePods);
        updateMaxExtraPods(values.maxExtraPods);
        updatePartition(values.partition);
        updateMinReadySeconds(values.minReadySeconds);
      }}
    >
      <FormItem label={msg.update_strategy}>
        <LargeSelect
          options={options}
          value={updateStrategy}
          onChange={(value) => {
            setUpdateStrategy(module, value);
          }}
        />
      </FormItem>
      {isDaemonSet && (
        <FormItem>
          <Card>
            <CheckExpandable type="fold" label={Header}>
              <RollUpdateSettingsContent>
                <FormItem
                  field="maxUnavailablePods"
                  required
                  label={msg.max_unavailable_pods}
                  extra={msg.max_unavailable_pods_desc}
                  rules={[
                    { required: true, message: msg.max_unavailable_pods_error_empty },
                    {
                      match: /^(\d+|\.\d+)(%)?$/,
                      message: msg.max_unavailable_pods_error_invalid,
                    },
                  ]}
                >
                  <Input />
                </FormItem>
                <FormItem
                  field="minReadySeconds"
                  required
                  label={msg.min_ready_seconds}
                  extra={msg.min_ready_seconds_desc}
                  rules={[{ required: true, message: msg.min_ready_seconds_error_empty }]}
                >
                  <InputNumber />
                </FormItem>
              </RollUpdateSettingsContent>
            </CheckExpandable>
          </Card>
        </FormItem>
      )}
      {isStatefulSet && (
        <FormItem>
          <Card>
            <CheckExpandable type="fold" label={Header}>
              <RollUpdateSettingsContent>
                <FormItem
                  field="partition"
                  required
                  label={msg.partition}
                  extra={msg.partition_desc}
                  rules={[{ required: true, message: msg.partition_error_empty }]}
                >
                  <InputNumber precision={0} step={1} min={0} />
                </FormItem>
              </RollUpdateSettingsContent>
            </CheckExpandable>
          </Card>
        </FormItem>
      )}
      {isDeployment && (
        <FormItem>
          <Card>
            <CheckExpandable type="fold" label={Header}>
              <RollUpdateSettingsContent>
                <FormItem
                  field="maxUnavailablePods"
                  required
                  label={msg.max_unavailable_pods}
                  extra={msg.max_unavailable_pods_desc}
                  rules={[
                    { required: true, message: msg.max_unavailable_pods_error_empty },
                    {
                      match: /^(\d+|\.\d+)(%)?$/,
                      message: msg.max_unavailable_pods_error_invalid,
                    },
                  ]}
                >
                  <Input />
                </FormItem>
                <FormItem
                  field="maxExtraPods"
                  required
                  label={msg.max_extra_pods}
                  extra={msg.max_extra_pods_desc}
                  rules={[
                    { required: true, message: msg.max_extra_pods_error_empty },
                    {
                      match: /^(\d+|\.\d+)(%)?$/,
                      message: msg.max_extra_pods_error_invalid,
                    },
                  ]}
                >
                  <Input />
                </FormItem>
              </RollUpdateSettingsContent>
            </CheckExpandable>
          </Card>
        </FormItem>
      )}
    </Form>
  );
}

/** ============================ 样式 ==================================== */
const RollUpdateSettingsContent = styled(GrayBox)`
  margin-top: 16px;
`;
